import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  endOfDay,
  format,
  isMatch,
  isValid,
  parse,
  startOfDay,
  startOfYear,
  subDays,
} from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import { DateFilterState, DatePreset } from '../types/metrics-type';

const getPresetDates = (
  preset: Exclude<DatePreset, 'custom'>,
): { startDate: Date; endDate: Date } => {
  const now = new Date();
  const endDate = endOfDay(now);

  switch (preset) {
    case 'allTime': {
      return { startDate: new Date(0), endDate };
    }
    case 'last7days': {
      return { startDate: startOfDay(subDays(now, 6)), endDate };
    }
    case 'last30days': {
      return { startDate: startOfDay(subDays(now, 29)), endDate };
    }
    case 'last90days': {
      return { startDate: startOfDay(subDays(now, 89)), endDate };
    }
    case 'thisYear': {
      return { startDate: startOfYear(now), endDate };
    }
    default:
      return { startDate: new Date(now), endDate: new Date(now) };
  }
};

export function useDateFilter(initialPreset: DatePreset = 'allTime') {
  const safeInitialPreset = initialPreset === 'custom' ? 'allTime' : initialPreset;
  const initialDates = getPresetDates(safeInitialPreset);

  const [dateFilter, setDateFilter] = useState<DateFilterState>({
    preset: initialPreset,
    ...initialDates,
  });

  const setPreset = useCallback((preset: DatePreset) => {
    const dates = getPresetDates(preset === 'custom' ? 'allTime' : preset);
    setDateFilter({
      preset,
      ...dates,
    });
  }, []);

  const setCustomDates = useCallback((startDate: Date, endDate: Date) => {
    setDateFilter({
      preset: 'custom',
      startDate,
      endDate,
    });
  }, []);

  const resetFilter = useCallback(() => {
    const dates = getPresetDates('allTime');
    setDateFilter({
      preset: 'allTime',
      ...dates,
    });
  }, []);

  return {
    dateFilter,
    setPreset,
    setCustomDates,
    lastWeekDates: getPresetDates('last7days'),
    lastMonthDates: getPresetDates('last30days'),
    resetFilter,
  };
}

type ParsedRouteRange =
  | { status: 'absent' }
  | { status: 'invalid' }
  | { status: 'valid'; from: string; to: string; startDate: Date; endDate: Date };

export function getValidDateRangeParams(
  from?: string | null,
  to?: string | null,
): { from: string; to: string } | undefined {
  const range = parseRouteRange(from ?? null, to ?? null);
  return range.status === 'valid' ? { from: range.from, to: range.to } : undefined;
}

export function useUsageDateFilter(enabled: boolean) {
  const dateFilterModel = useDateFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const routeFrom = searchParams.get('from');
  const routeTo = searchParams.get('to');
  const routeRange = useMemo(() => parseRouteRange(routeFrom, routeTo), [routeFrom, routeTo]);
  const routeMatchesFilter =
    routeRange.status === 'valid' &&
    dateFilterModel.dateFilter.preset !== 'allTime' &&
    format(dateFilterModel.dateFilter.startDate, 'yyyy-MM-dd') === routeRange.from &&
    format(dateFilterModel.dateFilter.endDate, 'yyyy-MM-dd') === routeRange.to;
  const isHydrating =
    enabled &&
    (routeRange.status === 'invalid' ||
      (routeRange.status === 'absent'
        ? dateFilterModel.dateFilter.preset !== 'allTime'
        : !routeMatchesFilter));

  useEffect(() => {
    if (!enabled) return;

    if (routeRange.status === 'absent') {
      if (dateFilterModel.dateFilter.preset !== 'allTime') dateFilterModel.resetFilter();
      return;
    }

    if (routeRange.status === 'invalid') {
      dateFilterModel.resetFilter();
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete('from');
      nextParams.delete('to');
      setSearchParams(nextParams, { replace: true });
      return;
    }

    if (!routeMatchesFilter) {
      dateFilterModel.setCustomDates(routeRange.startDate, routeRange.endDate);
    }
  }, [
    dateFilterModel.dateFilter.preset,
    dateFilterModel.resetFilter,
    dateFilterModel.setCustomDates,
    enabled,
    routeMatchesFilter,
    routeRange,
    searchParams,
    setSearchParams,
  ]);

  const syncRouteRange = useCallback(
    (range?: { startDate: Date; endDate: Date }) => {
      const nextParams = new URLSearchParams(searchParams);
      if (!range) {
        nextParams.delete('from');
        nextParams.delete('to');
      } else {
        nextParams.set('from', format(range.startDate, 'yyyy-MM-dd'));
        nextParams.set('to', format(range.endDate, 'yyyy-MM-dd'));
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPreset = useCallback(
    (preset: DatePreset) => {
      dateFilterModel.setPreset(preset);
      if (preset === 'custom') return;
      syncRouteRange(preset === 'allTime' ? undefined : getPresetDates(preset));
    },
    [dateFilterModel.setPreset, syncRouteRange],
  );

  const setCustomDates = useCallback(
    (startDate: Date, endDate: Date) => {
      dateFilterModel.setCustomDates(startDate, endDate);
      syncRouteRange({ startDate, endDate });
    },
    [dateFilterModel.setCustomDates, syncRouteRange],
  );

  return {
    ...dateFilterModel,
    setPreset,
    setCustomDates,
    isHydrating,
  };
}

function parseRouteRange(from: string | null, to: string | null): ParsedRouteRange {
  if (!from && !to) return { status: 'absent' };
  if (!from || !to || !isMatch(from, 'yyyy-MM-dd') || !isMatch(to, 'yyyy-MM-dd')) {
    return { status: 'invalid' };
  }

  const startDate = startOfDay(parse(from, 'yyyy-MM-dd', new Date()));
  const endDate = endOfDay(parse(to, 'yyyy-MM-dd', new Date()));
  if (!isValid(startDate) || !isValid(endDate) || startDate > endDate) {
    return { status: 'invalid' };
  }

  return { status: 'valid', from, to, startDate, endDate };
}
